// ─── Post-ramp sending pace ──────────────────────────────────────────────────
// Sits directly under WarmupRampBanner on the campaigns page and is deliberately
// its successor: the warm-up banner explains the ceiling a NEW account is under
// and disappears on day 22, at which point this appears and hands the customer the
// ceiling instead. The two are mutually exclusive by construction - both read the
// same `get_warmup_window` anchor, and this one renders NOTHING while `in_ramp` is
// true, so a customer never sees two competing limits.
//
// It lives here rather than in Settings for the same reason TcpaHoursBanner does:
// nobody revisits a settings page they have already filled in, and this is a
// decision that only becomes relevant on the day the ramp ends. It is a lever
// attached to the thing it governs.
//
// Reads and writes through the `tenant-pacing` edge function. `tenant_pacing` is
// service-role only (RLS on, no browser policy), so there is no direct table path.
//
// LOAD: one fetch on mount, one after each save, no interval. Prod is a 2 vCPU
// burstable box, and a polling effect behind the campaigns page is exactly the
// shape that put 66% of all database CPU into one browser tab on 2026-08-27.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Gauge,
  Zap,
  CalendarRange,
  SlidersHorizontal,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/permissions';

const num = (n) => (n == null ? null : Number(n).toLocaleString());
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Every figure on this panel is arithmetically correct and several of them used to
// read as nonsense anyway: "use your full allowance in roughly 1,453 days" when the
// cycle resets in four, "about 0 messages a day" for a light sender, "in about
// 0 days", "with 1 days to go". A number a customer reads twice costs more trust
// than it buys. These two helpers keep the small end of every range honest.

// "today" rather than "in roughly 0.1 days". Anything under a day IS today.
const daysPhrase = (d) => (d == null ? null : d < 1 ? 'today' : `in roughly ${plural(d, 'day')}`);

// Round to a precision the reader will believe: hundreds when it is large, tens
// when it is small, exact under ten. Stops "roughly 0 messages to spare" from
// appearing when there are 40 of them.
const roughly = (n) => {
  if (n == null) return null;
  if (n >= 1000) return Math.round(n / 100) * 100;
  if (n >= 100) return Math.round(n / 10) * 10;
  return Math.round(n);
};

// "Sep 25" from a bare YYYY-MM-DD, parsed as local PARTS so a date-only string
// does not slip a day backwards the way new Date('2026-09-25') (UTC midnight)
// would in any negative-offset zone. Same helper shape WarmupRampBanner uses.
const formatDay = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const OPTIONS = [
  {
    value: 'spread_evenly',
    icon: CalendarRange,
    title: 'Spread Evenly',
    tag: 'Recommended',
    blurb: 'Divides what is left of your monthly allowance across the days left in your billing cycle, and recalculates every morning. Steady volume, and you do not run out early.',
  },
  {
    value: 'custom_cap',
    icon: SlidersHorizontal,
    title: 'Custom Daily Cap',
    tag: null,
    blurb: 'You choose how many outreach messages go out per day. Useful when your team can only handle so many replies at once.',
  },
  {
    value: 'max_speed',
    icon: Zap,
    title: 'Maximum Speed',
    tag: null,
    blurb: 'No daily limit. Your list goes out as fast as the queue can send it.',
  },
];

const MAX_SPEED_WARNING =
  'Sending at maximum speed can affect carrier trust scores and deliverability over time. We recommend Spread Evenly unless you have a specific reason to send fast.';

const LABELS = {
  spread_evenly: 'Spread Evenly',
  custom_cap: 'Custom Daily Cap',
  max_speed: 'Maximum Speed',
};

export default function SendingPaceBanner() {
  const { hasPermission, loading: authLoading } = useAuth();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  const [selected, setSelected] = useState('spread_evenly');
  const [capInput, setCapInput] = useState('');
  const [confirmingMaxSpeed, setConfirmingMaxSpeed] = useState(false);

  const canEdit = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);

  const load = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/tenant-pacing`,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (!res.ok) return;

      const json = await res.json();
      setStatus(json);

      // An unconfigured tenant is genuinely uncapped today, so the API honestly
      // reports max_speed. We still PRESELECT Spread Evenly as the recommendation,
      // and the copy below says plainly that nothing is applied until they save.
      // Never render a recommendation as though it were already live.
      setSelected(json?.has_row ? json.mode : 'spread_evenly');
      setCapInput(json?.mode === 'custom_cap' && json?.daily_cap ? String(json.daily_cap) : '');
      // ALWAYS start compact. This banner sits above the fold on Campaign
      // Management, and expanded it was taking roughly 60% of the initial
      // viewport for the seven tenants who have not chosen a pace yet - i.e.
      // almost everyone. The unset state stays visible as an amber "Not set"
      // chip plus a Choose button, which is enough to read as an open decision
      // without pushing the campaign list off the screen.
    } catch {
      // Informational surface. A failed read renders nothing rather than an error
      // box on the campaigns page, matching WarmupRampBanner.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!authLoading) load(); }, [authLoading, load]);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const save = async (mode, extra = {}) => {
    if (!canEdit) { setError('You do not have permission to change the sending pace.'); return; }
    setSaving(true); setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/tenant-pacing`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode, ...extra }),
        },
      );
      const json = await res.json();
      if (!res.ok) { setError(json?.error || 'Could not save your sending pace.'); return; }

      setStatus(json);
      setSelected(json.mode);
      setCapInput(json.mode === 'custom_cap' && json.daily_cap ? String(json.daily_cap) : '');
      setConfirmingMaxSpeed(false);
      setCollapsed(true);
    } catch (e) {
      setError(`Could not save your sending pace: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) return null;
  if (!status?.available) return null;

  // The ramp owns the ceiling for the first 21 days and WarmupRampBanner is
  // rendered immediately above this. Showing an adjustable limit next to a fixed
  // one would read as though the fixed one were negotiable.
  if (status.in_ramp) return null;

  // Nothing to pace against.
  if (status.monthly_limit == null) return null;

  const {
    monthly_limit: limit,
    used_this_cycle: used,
    remaining_this_cycle: remaining,
    days_remaining: daysLeft,
    days_to_exhaust: burnDays,
    avg_per_day: avgPerDay,
    spread_preview: spreadPreview,
    cycle_end: cycleEnd,
    daily_cap: activeCap,
    used_today: usedToday,
    has_row: hasRow,
    mode: liveMode,
  } = status;

  // 🔴 `liveMode` is what is ENFORCED, and for a tenant who has chosen nothing the
  // API honestly reports 'max_speed' because that genuinely is their behaviour.
  // `chosenMode` is what the customer has actually PICKED, and is null until they
  // pick something. The two are only ever equal once a row exists.
  //
  // Conflating them shipped a dead end on 2026-08-28: the acknowledgement panel was
  // gated on `liveMode !== 'max_speed'` to avoid re-prompting someone already on
  // maximum speed, which is also true of the DEFAULT, so a tenant with no row who
  // clicked Maximum Speed got neither the acknowledgement nor a Save button. That
  // was 7 of 8 live tenants. Anything asking "has the customer chosen X" must read
  // `chosenMode`; only "what is the ceiling right now" may read `liveMode`.
  const chosenMode = hasRow ? liveMode : null;

  const dirty = selected !== chosenMode
    || (selected === 'custom_cap' && String(activeCap ?? '') !== capInput.trim());

  const capNumber = Number(capInput);
  const capValid = Number.isInteger(capNumber) && capNumber > 0 && capNumber <= 100000;

  // Only claim the allowance runs out early when the arithmetic says so. burnDays
  // is this cycle's own spend rate, not a forecast model.
  const willRunOutEarly = burnDays != null && daysLeft != null && burnDays < daysLeft;

  // What they will have left at the cycle end if today's pace holds.
  const projectedSpare = avgPerDay != null && daysLeft != null && remaining != null
    ? Math.max(roughly(remaining - avgPerDay * daysLeft), 0)
    : null;

  // A sender averaging under one message a day rounds to zero, and "about 0
  // messages a day" reads as though the account is broken. Below that threshold
  // the per-day figure is dropped and only the outcome is stated.
  const paceKnown = avgPerDay != null && avgPerDay >= 1;
  const tone = !hasRow || willRunOutEarly
    ? { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', head: 'text-amber-900', body: 'text-amber-800' }
    : { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', head: 'text-blue-900', body: 'text-blue-800' };

  const headline = hasRow
    ? `Sending pace: ${LABELS[liveMode]}`
    : 'Choose how fast this account sends';

  const summary = hasRow
    ? [
        activeCap != null ? `${num(activeCap)} outreach a day` : 'no daily limit',
        `${num(usedToday)} sent today`,
        cycleEnd ? `resets ${formatDay(cycleEnd)}` : null,
      ].filter(Boolean).join(' · ')
    : `${num(remaining)} of ${num(limit)} messages left${daysLeft != null ? ` with ${plural(daysLeft, 'day')} to go` : ''}`;

  return (
    <div className={`${tone.box} border rounded-lg p-4`} role="status">
      <div className="flex items-start gap-3">
        <Gauge className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className={`font-medium ${tone.head} truncate`}>{headline}</h4>
              {!hasRow && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-amber-200 text-amber-900 flex-shrink-0">
                  Not set
                </span>
              )}
            </div>

            {/* Text affordance beside the chevron. A bare chevron is easy to miss
                on a banner that is collapsed by default, and this is a lever we
                want found. Chevron kept so the control matches WarmupRampBanner,
                which is the collapse pattern already in this codebase. */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className={`text-xs font-medium ${tone.body} hover:opacity-70 underline underline-offset-2`}
              >
                {collapsed ? (hasRow ? 'Edit' : 'Choose') : 'Done'}
              </button>
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className={`${tone.body} hover:opacity-70`}
                aria-label={collapsed ? 'Show sending pace options' : 'Hide sending pace options'}
              >
                {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <p className={`text-sm ${tone.body} mt-1`}>{summary}</p>

          {!collapsed && (
            <>
              {/* The concrete tradeoff, from this cycle's real numbers.
                  ⚠️ NEVER show the raw days-to-exhaust figure when it lands past the
                  end of the cycle. An allowance does not carry over, so "you will
                  use your full allowance in 1,543 days" is arithmetically true and
                  meaningless: the cycle resets in days, not years. A light sender
                  saw exactly that on 2026-08-28. Past the cycle end the honest and
                  useful statement is how much they will have LEFT OVER. */}
              {willRunOutEarly && burnDays != null ? (
                <p className={`text-sm ${tone.body} mt-2`}>
                  {paceKnown ? <>At about <strong>{num(avgPerDay)}</strong> messages a day you will use</> : 'You will use'}
                  {' '}your full monthly allowance <strong>{daysPhrase(burnDays)}</strong>
                  {cycleEnd
                    ? `, ${plural(daysLeft - Math.floor(burnDays), 'day')} before your cycle resets on ${formatDay(cycleEnd)}.`
                    : '.'}
                </p>
              ) : projectedSpare != null ? (
                <p className={`text-sm ${tone.body} mt-2`}>
                  {paceKnown
                    ? <>At about <strong>{num(avgPerDay)}</strong> messages a day you are on track to finish</>
                    : <>You have used very little of your allowance this cycle. At this rate you will finish</>}
                  {' '}with roughly <strong>{num(projectedSpare)}</strong> messages to spare
                  {cycleEnd ? ` when it resets on ${formatDay(cycleEnd)}.` : '.'}
                </p>
              ) : null}

              {!hasRow && (
                <p className={`text-sm ${tone.body} mt-2`}>
                  Nothing is limiting you right now. Pick an option and save to change that.
                </p>
              )}

              {error && (
                <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              {/* Options */}
              <div className="mt-3 space-y-2">
                {OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = selected === opt.value;
                  const isLive = chosenMode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={!canEdit || saving}
                      onClick={() => {
                        setSelected(opt.value);
                        setConfirmingMaxSpeed(opt.value === 'max_speed');
                      }}
                      className={`w-full text-left rounded-lg border p-3 transition-colors bg-white ${
                        active ? 'border-slate-400 ring-1 ring-slate-300' : 'border-slate-200 hover:bg-slate-50'
                      } ${!canEdit || saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <Icon style={{ width: 16, height: 16 }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="font-medium text-slate-900 text-sm">{opt.title}</span>
                            {opt.tag && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-emerald-100 text-emerald-700">
                                {opt.tag}
                              </span>
                            )}
                            {isLive && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-slate-200 text-slate-600">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-slate-600">{opt.blurb}</p>
                          {opt.value === 'spread_evenly' && spreadPreview != null && (
                            <p className="mt-1.5 text-xs font-medium text-slate-800">
                              {spreadPreview >= 1
                                ? `That is about ${num(spreadPreview)} outreach messages a day right now.`
                                : `Your allowance for this cycle is already used up, so this would not resume sending until it resets${cycleEnd ? ` on ${formatDay(cycleEnd)}` : ''}.`}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Custom cap input */}
              {selected === 'custom_cap' && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                  <label htmlFor="pace-cap" className="block text-sm font-medium text-slate-700">
                    Outreach messages per day
                  </label>
                  <input
                    id="pace-cap"
                    type="number"
                    min="1"
                    max="100000"
                    value={capInput}
                    disabled={!canEdit || saving}
                    onChange={(e) => setCapInput(e.target.value)}
                    placeholder={spreadPreview != null ? String(spreadPreview) : '1000'}
                    className="mt-2 w-full sm:w-48 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                  />
                  {capValid && daysLeft != null && (
                    <p className="mt-2 text-xs text-slate-600">
                      {capNumber * daysLeft <= (remaining ?? 0)
                        ? `At ${num(capNumber)} a day you will still have allowance left when the cycle resets.`
                        : (() => {
                            const d = Math.floor((remaining ?? 0) / capNumber);
                            return `At ${num(capNumber)} a day you would use your remaining ${num(remaining)} messages ${d < 1 ? 'in under a day' : `in about ${plural(d, 'day')}`}, before the cycle resets.`;
                          })()}
                    </p>
                  )}
                  {capInput && !capValid && (
                    <p className="mt-2 text-xs text-red-600">Enter a whole number between 1 and 100,000.</p>
                  )}
                </div>
              )}

              {/* Maximum Speed acknowledgement. One click. Also enforced server-side,
                  so this dialog is not the only thing standing in front of it. */}
              {selected === 'max_speed' && confirmingMaxSpeed && chosenMode !== 'max_speed' && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-100/70 p-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-amber-900">{MAX_SPEED_WARNING}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={!canEdit || saving}
                          onClick={() => save('max_speed', { acknowledge_risk: true })}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60 inline-flex items-center gap-1.5"
                        >
                          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          I understand, use maximum speed
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => {
                            setConfirmingMaxSpeed(false);
                            setSelected(chosenMode ?? 'spread_evenly');
                          }}
                          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save. Maximum Speed saves from its own acknowledgement instead. */}
              {selected !== 'max_speed' && (
                <div className="mt-3 flex items-center justify-end gap-3">
                  {dirty && <span className={`text-xs ${tone.body}`}>Unsaved</span>}
                  <button
                    type="button"
                    disabled={!canEdit || saving || !dirty || (selected === 'custom_cap' && !capValid)}
                    onClick={() => save(selected, selected === 'custom_cap' ? { daily_cap: capNumber } : {})}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Save sending pace
                  </button>
                </div>
              )}

              <p className="mt-2 text-xs text-slate-500">
                A daily pace applies to outreach only. Replies to leads who write to you are never held back by it.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
